import React, { useState, useCallback, memo, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Alert,
  Fade,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { appViewSetUser } from '../app/store/appViewSlice';
import { userService } from '../services/userService';

interface WelcomeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userData: {
    first_name: string;
    last_name: string;
    email: string;
    image: string;
    userId?: string;
  };
}

// Create a memoized input component to prevent focus loss
const StableTextField = memo(
  ({
    label,
    value,
    onChange,
    error,
    helperText,
    required,
  }: {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: boolean;
    helperText?: string;
    required?: boolean;
  }) => (
    <TextField
      fullWidth
      label={label}
      variant="outlined"
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      required={required}
      margin="normal"
      inputProps={{ dir: 'rtl' }}
      InputLabelProps={{
        style: { right: 0, left: 'auto', transformOrigin: 'right' },
      }}
    />
  ),
  // Use a custom comparison to prevent unnecessary rerenders
  (prevProps, nextProps) => {
    return (
      prevProps.value === nextProps.value &&
      prevProps.error === nextProps.error &&
      prevProps.helperText === nextProps.helperText
    );
  },
);

const WelcomeDialog: React.FC<WelcomeDialogProps> = memo(
  ({ isOpen, onClose, userData }) => {
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Use useMemo to create the initial form state only once when userData changes
    const initialFormData = useMemo(
      () => ({
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        companyName: '',
      }),
      [userData.first_name, userData.last_name],
    );

    const [formData, setFormData] = useState(initialFormData);

    const updateFirstName = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, firstName: e.target.value }));
      },
      [],
    );

    const updateLastName = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, lastName: e.target.value }));
      },
      [],
    );

    const updateCompanyName = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, companyName: e.target.value }));
      },
      [],
    );

    const handleSubmit = useCallback(async () => {
      setIsSubmitting(true);
      setError('');

      try {
        // Update user profile in the backend
        const success = await userService.updateUserProfile({
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName,
        });

        if (success) {
          // Update Redux store
          dispatch(
            appViewSetUser({
              ...userData,
              first_name: formData.firstName,
              last_name: formData.lastName,
            }),
          );

          // Small delay for better UX
          await new Promise((resolve) => setTimeout(resolve, 800));

          // Close dialog
          onClose();

          // Refresh data in the background
          userService.refreshUserData(dispatch).catch((err) => {
            console.error('Failed to refresh data after onboarding:', err);
          });
        } else {
          setError('לא הצלחנו לעדכן את הפרופיל שלך. אנא נסה שוב.');
        }
      } catch (error) {
        console.error('Failed to update user data:', error);
        setError('אירעה שגיאה בעדכון הפרופיל. אנא נסה שוב מאוחר יותר.');
      } finally {
        setIsSubmitting(false);
      }
    }, [formData, dispatch, userData, onClose]);

    return (
      <Dialog
        open={isOpen}
        fullWidth
        maxWidth="sm"
        PaperComponent={(props) => (
          <Paper
            {...props}
            sx={{
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            }}
          />
        )}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <Typography variant="h4" dir="rtl" fontWeight="bold">
            ברוכים הבאים לחשבוניתה!
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" paragraph dir="rtl" textAlign="center">
              אנחנו שמחים שבחרתם להצטרף אלינו. בואו נכיר אותכם קצת יותר טוב
              &#129299;
            </Typography>
            <Typography variant="body1" paragraph dir="rtl" textAlign="center">
              בזמן שאתם ממלאים את הפרטים, אנחנו כבר מתחילים לעבוד על הקבלות
              שלכם. ניהול חשבוניות אמור להיות קל , מהיר וגם יפה על הדרך
            </Typography>
          </Box>

          {error && (
            <Fade in={!!error}>
              <Alert severity="error" sx={{ mb: 3 }} dir="rtl">
                {error}
              </Alert>
            </Fade>
          )}

          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              m: ({ spacing }) => `${spacing(4)} auto 0`,
              width: '75%',
            }}
            dir="rtl"
          >
            <StableTextField
              label="שם פרטי"
              value={formData.firstName}
              onChange={updateFirstName}
              error={!!error}
              required
            />

            <StableTextField
              label="שם משפחה"
              value={formData.lastName}
              onChange={updateLastName}
              error={!!error}
              required
            />

            <StableTextField
              label="שם החברה"
              value={formData.companyName}
              onChange={updateCompanyName}
              error={!!error}
              required
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
          <LoadingButton
            variant="contained"
            color="primary"
            // size="large"
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={!formData.firstName || !formData.lastName}
            sx={{
              minWidth: 200,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {'התחל'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

export default WelcomeDialog;
